import { useState, useEffect } from 'react';
import { gameApi } from '../services/api';
import type { HackAttackType, HackLogEntry } from '../types';

const COOLDOWN_MS = 30 * 60 * 1000;

function fmtCountdown(ms: number): string {
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

interface CooldownEntry {
  ip: string;
  username: string;
  timestamp: string;
  attackType: HackAttackType;
  success: boolean;
  msLeft: number;
}

export default function CooldownTargets() {
  const [hackLog, setHackLog] = useState<HackLogEntry[] | null>(null);
  const [, forceUpdate] = useState(0);

  // Load the hack log on mount so a direct page load / F5 refresh works. The log
  // now lives in a dedicated Cosmos container, fetched via its own endpoint.
  useEffect(() => {
    gameApi.getHackLog()
      .then(res => setHackLog(res.data as HackLogEntry[]))
      .catch(() => setHackLog([]));
  }, []);

  // Refresh countdown every second
  useEffect(() => {
    const id = setInterval(() => forceUpdate(n => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (hackLog === null) {
    return (
      <div className="container">
        <p style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>Loading...</p>
      </div>
    );
  }

  const now = Date.now();

  // Build map: IP → latest FAILED attack within cooldown window.
  // Successful attacks do not trigger cooldown.
  // The first Whois NPC (10.0.0.1) is always exempt — skip it.
  const FIRST_WHOIS_IP = '10.0.0.1';
  const map = new Map<string, CooldownEntry>();
  for (const e of hackLog) {
    if (!e.wasAttacker) continue;
    if (e.success) continue;              // only failed attacks trigger cooldown
    if (e.opponentIp === FIRST_WHOIS_IP) continue; // first Whois is always accessible
    const ts = new Date(e.timestamp).getTime();
    const msLeft = COOLDOWN_MS - (now - ts);
    if (msLeft <= 0) continue;

    const existing = map.get(e.opponentIp);
    if (!existing || new Date(e.timestamp).getTime() > new Date(existing.timestamp).getTime()) {
      map.set(e.opponentIp, {
        ip:         e.opponentIp,
        username:   e.opponentUsername,
        timestamp:  e.timestamp,
        attackType: e.attackType,
        success:    e.success,
        msLeft,
      });
    }
  }

  const entries = Array.from(map.values()).sort((a, b) => b.msLeft - a.msLeft);

  return (
    <div className="container" style={{ maxWidth: '800px' }}>
      <div className="card">
        <div className="card-header">
          <h1 className="card-title" style={{ fontFamily: 'monospace' }}>Attack Cooldowns</h1>
          <p style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.85rem', margin: 0 }}>
            Targets you cannot attack for 30 minutes after your last attempt.
          </p>
        </div>

        {entries.length === 0 ? (
          <div style={{
            fontFamily: 'monospace',
            color: 'var(--text-muted)',
            padding: '2rem',
            textAlign: 'center',
            borderTop: '1px solid var(--border-color)',
          }}>
            No active cooldowns — all targets available.
          </div>
        ) : (
          <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'monospace' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                {['Target', 'IP', 'Last Attack', 'Result', 'Cooldown remaining'].map(h => (
                  <th key={h} style={{
                    padding: '0.5rem 0.75rem',
                    textAlign: 'left',
                    color: 'var(--text-muted)',
                    fontSize: '0.78rem',
                    fontWeight: 'normal',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.ip} style={{ borderBottom: '1px solid #1e1e1e' }}>
                  <td style={{ padding: '0.55rem 0.75rem', color: '#e2e8f0' }}>
                    {e.username}
                  </td>
                  <td style={{ padding: '0.55rem 0.75rem', color: '#64748b', fontSize: '0.82rem' }}>
                    {e.ip}
                  </td>
                  <td style={{ padding: '0.55rem 0.75rem', color: '#64748b', fontSize: '0.82rem' }}>
                    {e.attackType}
                  </td>
                  <td style={{ padding: '0.55rem 0.75rem' }}>
                    <span style={{
                      color:      e.success ? '#4ade80' : '#f87171',
                      fontSize:   '0.8rem',
                    }}>
                      {e.success ? 'SUCCESS' : 'FAILED'}
                    </span>
                  </td>
                  <td style={{ padding: '0.55rem 0.75rem' }}>
                    <span style={{
                      color:     e.msLeft < 5 * 60 * 1000 ? '#4ade80' : '#fbbf24',
                      fontWeight: 'bold',
                      fontSize:  '0.85rem',
                    }}>
                      {fmtCountdown(Math.max(0, e.msLeft))}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
